import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import './Usuarios.css';

import API_BASE_URL from '../../../config/api';
const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Focus ref
  const nomeInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: ''
  });

  useEffect(() => {
    fetchUsuarios();
  }, []);

  // Auto-focus when modal opens
  useEffect(() => {
    if (showModal && nomeInputRef.current) {
      nomeInputRef.current.focus();
    }
  }, [showModal]);

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios`);
      if (!response.ok) throw new Error('Erro ao buscar usuários');
      const data = await response.json();
      
      // Map backend columns to frontend state
      const mappedUsuarios = data.map((u) => ({
        id: u.CD_USUARIO,
        nome: u.NM_USUARIO,
        email: u.DS_EMAIL,
        dataCadastro: u.DT_CADASTRO
      }));
      
      setUsuarios(mappedUsuarios);
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsuarios = usuarios.filter(usuario => {
    const term = searchTerm.toLowerCase();
    return (
      usuario.nome.toLowerCase().includes(term) ||
      usuario.email.toLowerCase().includes(term)
    );
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.nome || !formData.email) {
      alert('Nome e Email são obrigatórios');
      return;
    }

    if (!editingUsuario && !formData.senha) {
      alert('Senha é obrigatória para novos usuários');
      return;
    }

    if (formData.senha && formData.senha !== formData.confirmarSenha) {
      alert('As senhas não conferem');
      return;
    }

    const payload = {
      nome: formData.nome,
      email: formData.email,
      senha: formData.senha // Optional in backend for updates
    };

    try {
      const url = editingUsuario 
        ? `${API_BASE_URL}/usuarios/${editingUsuario.id}`
        : `${API_BASE_URL}/usuarios`;
      
      const method = editingUsuario ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao salvar');
      }

      await fetchUsuarios();
      resetForm();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert(error.message || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/usuarios/${id}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) throw new Error('Erro ao excluir');
        
        setUsuarios(usuarios.filter(u => u.id !== id));
      } catch (error) {
        console.error('Erro:', error);
        alert('Erro ao excluir usuário');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      senha: '',
      confirmarSenha: ''
    });
    setEditingUsuario(null);
    setShowModal(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleNewUser = () => {
    setFormData({
      nome: '',
      email: '',
      senha: '',
      confirmarSenha: ''
    });
    setEditingUsuario(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowModal(true);
  };

  const handleEdit = (usuario) => {
    setFormData({
      nome: usuario.nome,
      email: usuario.email,
      senha: '',
      confirmarSenha: ''
    });
    setEditingUsuario(usuario);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="tabela-referencia-container">
      {!showModal ? (
        <>
          <div className="tabela-header">
            <div className="header-content">
              <h1>Usuários</h1>
            </div>
            <button className="btn-primary" onClick={handleNewUser}>
              <FaPlus /> Novo Usuário
            </button>
          </div>

          <div className="table-container">
            <div className="table-controls">
              <div className="search-container">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Pesquisar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="table-info">
                {filteredUsuarios.length} registro(s) encontrado(s)
              </div>
            </div>

            <div className="table-wrapper">
              <table className="data-table usuarios-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Data Cadastro</th>
                    <th className="acoes-col">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                        Carregando...
                      </td>
                    </tr>
                  ) : filteredUsuarios.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                        Nenhum usuário encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredUsuarios.map((usuario) => (
                      <tr key={usuario.id}>
                        <td>{usuario.nome}</td>
                        <td>{usuario.email}</td>
                        <td>{formatDate(usuario.dataCadastro)}</td>
                        <td className="acoes-col">
                          <div className="action-buttons">
                            <button 
                              className="btn-edit"
                              onClick={() => handleEdit(usuario)}
                              title="Editar"
                            >
                              <FaEdit size={18} />
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => handleDelete(usuario.id)}
                              title="Excluir"
                            >
                              <FaTrash size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="tabela-header">
            <div className="header-content">
              <h1>{editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}</h1>
            </div>
            <button className="btn-secondary" onClick={() => setShowModal(false)}>
              Voltar
            </button>
          </div>

          <div className="content-card">
            <form onSubmit={handleSubmit}>
              <div className="form-content">
                <div className="form-group">
                  <label>Nome Completo</label>
                  <input
                    ref={nomeInputRef}
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="Digite o nome completo"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Digite o email"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group password-group">
                    <label>Senha {editingUsuario && '(Opcional)'}</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.senha}
                        onChange={(e) => setFormData({...formData, senha: e.target.value})}
                        placeholder={editingUsuario ? "Deixe em branco para manter" : "Digite a senha"}
                        style={{ paddingRight: '35px' }}
                      />
                      <button 
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group password-group">
                    <label>Confirmar Senha</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmarSenha}
                        onChange={(e) => setFormData({...formData, confirmarSenha: e.target.value})}
                        placeholder="Confirme a senha"
                        style={{ paddingRight: '35px' }}
                      />
                      <button 
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingUsuario ? 'Salvar Alterações' : 'Cadastrar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Usuarios;
